import type { Message, Part } from "@mux/sdk/v2/client"
import { produce, reconcile, type SetStoreFunction, type Store } from "solid-js/store"
import { cmpMessage, cmpPart, isOutlinePart, mergeStreamingPart } from "./session/message-reducer"
import { mergeMessageTurnDiff } from "./session/turn-diff-message"
import { parsePartDeltaEvent, type PartDeltaEvent } from "./event-validation"
import { appendPositionedOutput, appendPositionedText, byteLength } from "./positioned-text"
import type { GlobalStore } from "./types"

/** Drops what the store keeps beside a deleted or evicted message: its parts and actor. */
export function removeMessageData(draft: GlobalStore, messageIDs: Iterable<string>) {
  for (const messageID of messageIDs) {
    delete draft.part[messageID]
    delete draft.message_actor?.[messageID]
  }
}

export function applyMessageEvent(input: {
  event: { type: string; properties?: unknown }
  store: Store<GlobalStore>
  setStore: SetStoreFunction<GlobalStore>
  onTextGap?: (sessionID: string, delta: PartDeltaEvent["properties"]) => void
}) {
  const event = input.event
  switch (event.type) {
    case "message.updated": {
      const info = (event.properties as { info: Message }).info
      if (info.role === "user") {
        const actorID = (event as { actorID?: unknown }).actorID
        if (typeof actorID === "string" && actorID) input.setStore("message_actor", info.id, actorID)
      }
      const messages = input.store.message[info.sessionID]
      if (!messages) input.setStore("message", info.sessionID, [info])
      else {
        const index = messages.findIndex((message) => message.id === info.id)
        if (index !== -1) {
          const merged = mergeMessageTurnDiff(info, messages[index])
          const before = messages[index - 1]
          const after = messages[index + 1]
          if ((!before || cmpMessage(before, merged) <= 0) && (!after || cmpMessage(merged, after) <= 0)) {
            input.setStore("message", info.sessionID, index, reconcile(merged))
          } else {
            // A prompt that waited behind a run is created again when it starts.
            input.setStore("message", info.sessionID, produce((draft) => {
              draft.splice(index, 1)
              const insert = draft.findIndex((message) => cmpMessage(message, merged) > 0)
              draft.splice(insert === -1 ? draft.length : insert, 0, merged)
            }))
          }
        }
        else {
          const insert = messages.findIndex((message) => cmpMessage(message, info) > 0)
          input.setStore("message", info.sessionID, produce((draft) => {
            draft.splice(insert === -1 ? messages.length : insert, 0, info)
          }))
        }
      }
      return true
    }
    case "message.removed": {
      const props = event.properties as { sessionID: string; messageID: string }
      input.setStore(produce((draft) => {
        const messages = draft.message[props.sessionID]
        const index = messages?.findIndex((message) => message.id === props.messageID) ?? -1
        if (messages && index !== -1) messages.splice(index, 1)
        removeMessageData(draft, [props.messageID])
      }))
      return true
    }
    case "message.part.updated": {
      const part = (event.properties as { part: Part }).part
      const parts = input.store.part[part.messageID]
      if (!parts) input.setStore("part", part.messageID, [part])
      else {
        const index = parts.findIndex((item) => item.id === part.id)
        if (index !== -1) {
          const merged = mergeStreamingPart(parts[index]!, part)
          const before = parts[index - 1]
          const after = parts[index + 1]
          if ((!before || cmpPart(before, merged) <= 0) && (!after || cmpPart(merged, after) <= 0)) {
            input.setStore("part", part.messageID, index, reconcile(merged))
          } else {
            input.setStore("part", part.messageID, produce((draft) => {
              draft.splice(index, 1)
              const insertion = draft.findIndex((item) => cmpPart(merged, item) < 0)
              draft.splice(insertion === -1 ? draft.length : insertion, 0, merged)
            }))
          }
        } else {
          // A part an outline folds stays folded; opening the outline loads it.
          if (parts.some(isOutlinePart)) return true
          const insertion = parts.findIndex((item) => cmpPart(part, item) < 0)
          input.setStore("part", part.messageID, produce((draft) => draft.splice(insertion === -1 ? draft.length : insertion, 0, part)))
        }
      }
      return true
    }
    case "message.part.removed": {
      const props = event.properties as { messageID: string; partID: string }
      const parts = input.store.part[props.messageID]
      if (!parts) return true
      const index = parts.findIndex((part) => part.id === props.partID)
      if (index === -1) return true
      input.setStore(produce((draft) => {
        const list = draft.part[props.messageID]
        if (!list) return
        const next = list.findIndex((part) => part.id === props.partID)
        if (next === -1) return
        list.splice(next, 1)
        if (list.length === 0) delete draft.part[props.messageID]
      }))
      return true
    }
    case "message.part.delta": {
      const parsed = parsePartDeltaEvent(event)
      if (!parsed) return true
      const props = parsed.properties
      const parts = input.store.part[props.messageID]
      const index = parts?.findIndex((part) => part.id === props.partID) ?? -1
      if (!parts || index === -1) {
        if (props.field === "text" && props.sessionID) input.onTextGap?.(props.sessionID, props)
        return true
      }
      const current = parts[index]
      if ((current.type === "text" || current.type === "reasoning") && props.field === "text") {
        const text = appendPositionedText(current, current.text, props.delta, props.byteOffset)
        if (text === undefined) input.onTextGap?.(current.sessionID, props)
        else input.setStore("part", props.messageID, index, produce((part) => {
          if (part.type === "text" || part.type === "reasoning") part.text = text
        }))
        return true
      }
      input.setStore("part", props.messageID, produce((draft) => {
        const part = draft[index]
        // A streaming call's input grows until the call runs; its execution
        // publishes the full input, so a gap here only delays the preview.
        if (part.type === "tool" && part.state.status === "pending" && props.field.startsWith("state.input.")) {
          const key = props.field.slice("state.input.".length)
          const values = part.state.input as Record<string, unknown>
          const value = values[key]
          const next = appendPositionedText(part.state, typeof value === "string" ? value : "", props.delta, props.byteOffset)
          if (next !== undefined) values[key] = next
          return
        }
        if (part.type === "tool" && part.state.status === "running" && props.field === "state.output") {
          const state = part.state as typeof part.state & { output?: string; outputOffset?: number }
          const output = state.output ?? ""
          if (props.byteOffset === undefined) {
            state.output = output + props.delta
            return
          }
          // A watched chat's live tail replays deltas the running state may already show.
          const next = appendPositionedOutput(output, state.outputOffset ?? byteLength(output), props.delta, props.byteOffset)
          if (!next) return
          state.output = next.output
          state.outputOffset = next.covered
        }
      }))
      return true
    }
    default:
      return false
  }
}
